// P1-7 (Agent D): Employee detail page at /employees/:id.
//
// Surfaces:
//   - Identity card (name, email, role, status)
//   - Edit modal (name / role, idempotency-key)
//   - Access policy (schedule + zones)
//   - Status actions (suspend / reactivate / delete) with confirms
//   - Credentials list + Issue / Revoke + Fob enrollment flow

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, type ApiRequestInit } from '../lib/api.js';
import { useTenantScopedPage } from '../lib/useTenantScopedPage.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { Field, ModalShell } from '../components/Modal.js';
import { EnrollFobModal } from '../components/EnrollFobModal.js';
import { useAuth } from '../auth/AuthContext.js';
import { isValidPinCode, normalizePinCodeInput } from '../lib/pinCode.js';
import {
  credentialStatusLabel,
  credentialTypeLabel,
  employeeRoleLabel,
  statusLabel,
} from '../lib/humanLabels.js';

type EmployeeStatus = 'invited' | 'active' | 'suspended' | 'offboarded';
type SyncStatus = 'synced' | 'pending' | 'failed' | 'drifted' | 'in_flight';
type EmployeeRole = 'employee' | 'manager';
type CredentialType = 'nfc_card' | 'mobile_key' | 'pin_code';
type CredentialStatus = 'pending' | 'active' | 'revoked';

interface AccessActivitySummary {
  event_id: string;
  type: string;
  severity: string;
  occurred_at: string;
  door_name: string | null;
  unifi_device_id: string | null;
}

interface CredentialRow {
  id: string;
  type: CredentialType;
  status: CredentialStatus;
  label: string | null;
  card_token: string | null;
  unifi_credential_id: string | null;
  sync_status?: SyncStatus;
  last_sync_error?: string | null;
  created_at: string;
}

interface EmployeeDetail {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  role: EmployeeRole;
  status: EmployeeStatus;
  unifi_user_id: string | null;
  schedule_id: string | null;
  sync_status: SyncStatus;
  last_sync_error: string | null;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
  credentials: CredentialRow[];
  last_access?: AccessActivitySummary | null;
  recent_activity?: AccessActivitySummary[];
}

interface ScheduleSummary {
  id: string;
  name: string;
}

interface TouchPassRow {
  id: string;
  card_id: string | null;
  card_name: string | null;
  status: string;
  user_id: string | null;
}

interface EmployeeAccessZoneRow {
  id: string;
  name: string;
  schedule_id: string | null;
  sync_status: SyncStatus;
  controller_enforced: boolean;
  last_sync_error: string | null;
  assigned: boolean;
}

const STATUS_TONE: Record<EmployeeStatus, string> = {
  invited: 'bg-tertiary-fixed text-on-tertiary-fixed',
  active: 'bg-secondary-container text-on-secondary-container',
  suspended: 'bg-error-container text-on-error-container',
  offboarded: 'bg-surface-container-high text-on-surface-variant',
};

const CRED_STATUS_TONE: Record<CredentialStatus, string> = {
  pending: 'bg-tertiary-fixed text-on-tertiary-fixed',
  active: 'bg-secondary-container text-on-secondary-container',
  revoked: 'bg-surface-container-high text-on-surface-variant',
};

function accessZoneUpdateLabel(zone: EmployeeAccessZoneRow): string {
  if (zone.sync_status === 'failed') return 'Update failed';
  if (zone.sync_status === 'drifted') return 'Needs review';
  if (zone.sync_status === 'in_flight') return 'Updating';
  if (!zone.controller_enforced || zone.sync_status === 'pending') return 'Update pending';
  return 'Ready';
}

function activityTime(value: string): string {
  return new Date(value).toLocaleString();
}

function activityLocation(activity: AccessActivitySummary): string {
  return activity.door_name ?? 'Unknown door';
}

export function EmployeeDetailPage() {
  const { state } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [emp, setEmp] = useState<EmployeeDetail | null>(null);
  const { scopeOpts } = useTenantScopedPage(emp?.tenant_id);
  const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<CredentialRow | null>(null);
  const [statusAction, setStatusAction] = useState<null | 'suspend' | 'reactivate' | 'offboard'>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setEmp(null);
    setError(null);
    Promise.all([
      apiClient.get<{ data: EmployeeDetail }>(`/api/v1/employees/${id}`, scopeOpts),
      apiClient
        .get<{ data: ScheduleSummary[] }>('/api/v1/schedules', scopeOpts)
        .catch(() => ({ data: [] as ScheduleSummary[] })),
    ])
      .then(([d, s]) => {
        if (cancelled) return;
        setEmp(d.data);
        setSchedules(s.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load employee');
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick, scopeOpts]);

  const scheduleName = useMemo(() => {
    if (!emp?.schedule_id) return null;
    return schedules.find((s) => s.id === emp.schedule_id)?.name ?? null;
  }, [emp, schedules]);

  const touchPolicyEnabled =
    state.status === 'authed' ? state.me.credential_policy.touch_credential_enabled : false;
  const thirdPartyNfcEnabled =
    state.status === 'authed' ? state.me.credential_policy.third_party_nfc_enabled : false;
  // UX-034: PIN codes are an explicit site-wide opt-in, off by default and
  // administered outside the portal when off. That gate is deliberate — but
  // when an admin has switched them on, an operator working from a person's
  // record could still not issue one, because this page only knew about fobs
  // and Phone KeyPasses.
  const pinCodeEnabled =
    state.status === 'authed' ? state.me.credential_policy.pin_code_enabled : false;
  const enrollmentReaderMode =
    state.status === 'authed'
      ? (((state.me.credential_policy as Record<string, unknown>).enrollment_reader_mode as
          | string
          | undefined) ?? 'any')
      : 'any';
  const enrollmentReaderId =
    state.status === 'authed'
      ? (((state.me.credential_policy as Record<string, unknown>).enrollment_reader_id as
          | string
          | null
          | undefined) ?? null)
      : null;
  const enrollmentReaderName =
    state.status === 'authed'
      ? (((state.me.credential_policy as Record<string, unknown>).enrollment_reader_name as
          | string
          | null
          | undefined) ?? null)
      : null;
  const canIssue =
    !!emp &&
    emp.status !== 'offboarded' &&
    emp.status !== 'suspended' &&
    emp.sync_status === 'synced';
  const canEnroll = canIssue && thirdPartyNfcEnabled;
  const canIssuePhoneKeyPass = canIssue && touchPolicyEnabled;
  const canIssuePin = canIssue && pinCodeEnabled;
  const disabledByPolicy = [
    thirdPartyNfcEnabled ? null : 'Fob enrollment',
    touchPolicyEnabled ? null : 'Phone KeyPasses',
    pinCodeEnabled ? null : 'PIN codes',
  ].filter((label): label is string => label !== null);

  useEffect(() => {
    if (!emp) return;
    const wantsEdit = searchParams.get('edit') === '1';
    const wantsIssue = searchParams.get('issueCredential') === '1';
    if (!wantsEdit && !wantsIssue) return;

    const next = new URLSearchParams(searchParams);
    if (wantsEdit) {
      if (emp.status !== 'offboarded') setEditOpen(true);
      next.delete('edit');
    }
    if (wantsIssue) {
      if (canIssue) setIssueOpen(true);
      next.delete('issueCredential');
    }
    setSearchParams(next, { replace: true });
  }, [canIssue, emp, searchParams, setSearchParams]);

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/employees')}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Employees
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!emp) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to="/employees"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <Icon name="arrow_back" /> Employees
          </Link>
          <h1 className="font-headline-md text-headline-md text-on-surface">{emp.name}</h1>
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${STATUS_TONE[emp.status]}`}
            data-testid="emp-status-chip"
          >
            {statusLabel(emp.status)}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => setEditOpen(true)}
            disabled={emp.status === 'offboarded'}
            data-testid="emp-edit-btn"
          >
            <Icon name="edit" className="text-sm" />
            <span className="ml-1">Edit</span>
          </Button>
          {emp.status === 'suspended' ? (
            <Button
              variant="primary"
              onClick={() => setStatusAction('reactivate')}
              data-testid="emp-reactivate-btn"
            >
              Reactivate
            </Button>
          ) : emp.status !== 'offboarded' ? (
            <Button
              variant="secondary"
              onClick={() => setStatusAction('suspend')}
              data-testid="emp-suspend-btn"
            >
              Suspend
            </Button>
          ) : null}
          {emp.status !== 'offboarded' ? (
            <Button
              variant="danger"
              onClick={() => setStatusAction('offboard')}
              data-testid="emp-offboard-btn"
            >
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" onClick={() => setToast(null)} className="underline font-medium">
            Dismiss
          </button>
        </div>
      ) : null}

      {/* Identity card */}
      <section
        aria-label="Identity"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
      >
        <KV k="Email" v={emp.email} />
        <KV k="Role" v={employeeRoleLabel(emp.role)} />
        <KV
          k="Last Activity"
          v={
            emp.last_access
              ? `${activityTime(emp.last_access.occurred_at)} · ${activityLocation(emp.last_access)}`
              : 'Never Accessed'
          }
        />
      </section>

      <RecentActivityPanel activity={emp.recent_activity ?? []} />

      <EmployeeAccessZonesPanel
        emp={emp}
        scheduleName={scheduleName}
        schedules={schedules}
        onChanged={reload}
        onToast={setToast}
        scopeOpts={scopeOpts}
      />

      {/* Credentials */}
      <section
        aria-label="Credentials"
        className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden"
      >
        <div className="flex flex-col gap-4 px-4 py-4 border-b border-outline-variant lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="font-headline-sm text-headline-sm text-on-surface">Credentials</h2>
            {/* Naming what policy has switched off, rather than the previous
                nested ternary that only knew about two of the three. */}
            {/* Leading with the verb avoids agreement entirely. The previous
                form picked "is" for a single item, which produced "Phone
                KeyPasses is disabled by admin policy" — the labels are plural
                nouns, so no single choice of verb works for all of them. */}
            {disabledByPolicy.length > 0 ? (
              <p className="font-body-md text-body-md text-on-surface-variant mt-1">
                Disabled by admin policy:{' '}
                {disabledByPolicy.length === 1
                  ? disabledByPolicy[0]
                  : `${disabledByPolicy.slice(0, -1).join(', ')} and ${disabledByPolicy.at(-1)}`}
                .
              </p>
            ) : null}
          </div>
          {thirdPartyNfcEnabled || touchPolicyEnabled || pinCodeEnabled ? (
            <div className="grid w-full gap-2 sm:grid-cols-2 lg:w-auto lg:min-w-[440px]">
              {thirdPartyNfcEnabled ? (
                <button
                  type="button"
                  onClick={() => setEnrollOpen(true)}
                  disabled={!canEnroll}
                  data-testid="emp-enroll-btn"
                  title={
                    !canEnroll
                      ? 'Employee must be active and fully updated before enrolling a fob.'
                      : undefined
                  }
                  className="group flex min-h-16 items-center gap-3 rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-left transition-colors hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-surface-container text-on-surface group-hover:bg-surface-container-high">
                    <Icon name="contactless" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-label-md text-label-md text-on-surface">
                      Enroll fob/card
                    </span>
                    <span className="block font-body-sm text-body-sm text-on-surface-variant">
                      Scan at a reader
                    </span>
                  </span>
                </button>
              ) : null}
              {touchPolicyEnabled ? (
                <button
                  type="button"
                  onClick={() => setIssueOpen(true)}
                  disabled={!canIssuePhoneKeyPass}
                  data-testid="emp-issue-cred-btn"
                  title={
                    !canIssuePhoneKeyPass
                      ? 'Employee must be active and fully updated before issuing a Phone KeyPass.'
                      : undefined
                  }
                  className="group flex min-h-16 items-center gap-3 rounded-lg border border-primary bg-primary px-3 py-2 text-left text-on-primary transition-colors hover:bg-primary-container disabled:border-outline-variant disabled:bg-surface-container-high disabled:text-on-surface-variant disabled:cursor-not-allowed"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-on-primary/15 text-on-primary group-disabled:bg-surface-container group-disabled:text-on-surface-variant">
                    <Icon name="phone_iphone" fill />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-label-md text-label-md">Issue Phone KeyPass</span>
                    <span className="block font-body-sm text-body-sm opacity-85">
                      Assign a tenant phone pass
                    </span>
                  </span>
                </button>
              ) : null}
              {/* UX-034: PIN issuance exists, is policy-gated, and was
                  unreachable from a person's record — the operator had to
                  navigate to enrollment and re-pick the person they were
                  already looking at. This links into that flow pre-scoped
                  rather than duplicating its mutation, validation and
                  idempotency handling here. */}
              {pinCodeEnabled ? (
                <Link
                  to={
                    canIssuePin
                      ? `/credentials/enroll?type=pin_code&employeeId=${encodeURIComponent(emp.id)}${
                          emp.tenant_id ? `&tenantId=${encodeURIComponent(emp.tenant_id)}` : ''
                        }`
                      : '#'
                  }
                  aria-disabled={!canIssuePin}
                  data-testid="emp-issue-pin-btn"
                  title={
                    !canIssuePin
                      ? 'Employee must be active and fully updated before issuing a PIN code.'
                      : undefined
                  }
                  onClick={(e) => {
                    if (!canIssuePin) e.preventDefault();
                  }}
                  className={`group flex min-h-16 items-center gap-3 rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-left transition-colors ${
                    canIssuePin
                      ? 'hover:bg-surface-container-low'
                      : 'cursor-not-allowed opacity-50'
                  }`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-surface-container text-on-surface group-hover:bg-surface-container-high">
                    <Icon name="dialpad" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-label-md text-label-md text-on-surface">
                      Issue PIN code
                    </span>
                    <span className="block font-body-sm text-body-sm text-on-surface-variant">
                      Set a 4–8 digit code
                    </span>
                  </span>
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
        {emp.credentials.length === 0 ? (
          <div className="p-8 text-center text-on-surface-variant font-body-md text-body-md">
            No credentials yet.
          </div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-outline-variant">
              {emp.credentials
                .filter((c) => c.status !== 'revoked')
                .map((c) => (
                  <div key={c.id} data-testid={`cred-row-mobile-${c.id}`} className="px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-body-md text-body-md font-semibold text-on-surface">
                          {credentialTypeLabel(c.type, {
                            unifiCredentialId: c.unifi_credential_id,
                          })}
                        </p>
                        <p className="text-sm text-on-surface-variant break-words">
                          {c.label ?? 'No label'}
                        </p>
                      </div>
                      {c.status === 'active' || (c.status === 'pending' && c.unifi_credential_id) ? (
                        <button
                          type="button"
                          onClick={() => setRevokeTarget(c)}
                          className="inline-flex min-h-[1.5rem] items-center text-error hover:underline font-label-md text-label-md"
                          data-testid={`cred-revoke-btn-mobile-${c.id}`}
                        >
                          Revoke
                        </button>
                      ) : c.status === 'pending' && !c.unifi_credential_id ? (
                        <button
                          type="button"
                          onClick={() => setRevokeTarget(c)}
                          className="inline-flex min-h-[1.5rem] items-center text-on-surface-variant hover:underline font-label-md text-label-md"
                          data-testid={`cred-cancel-btn-mobile-${c.id}`}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${CRED_STATUS_TONE[c.status]}`}
                      >
                        {credentialStatusLabel(c.status)}
                      </span>
                      <span className="text-sm text-on-surface-variant">
                        Created {new Date(c.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-container">
                  <tr>
                    <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">
                      Type
                    </th>
                    <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">
                      Label
                    </th>
                    <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">
                      Status
                    </th>
                    <th className="text-right px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">
                      Created
                    </th>
                    <th className="text-right px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">
                      &nbsp;
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {emp.credentials
                    .filter((c) => c.status !== 'revoked')
                    .map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`cred-row-${c.id}`}
                        className="hover:bg-surface-container-low"
                      >
                        <td className="px-4 py-2 font-body-md text-body-md text-on-surface">
                          {credentialTypeLabel(c.type, {
                            unifiCredentialId: c.unifi_credential_id,
                          })}
                        </td>
                        <td className="px-4 py-2 font-body-md text-body-md text-on-surface">
                          {c.label ?? '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${CRED_STATUS_TONE[c.status]}`}
                          >
                            {credentialStatusLabel(c.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-body-md text-body-md text-on-surface-variant">
                          {new Date(c.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {c.status === 'active' || (c.status === 'pending' && c.unifi_credential_id) ? (
                            <button
                              type="button"
                              onClick={() => setRevokeTarget(c)}
                              className="inline-flex min-h-[1.5rem] items-center text-error hover:underline font-label-md text-label-md"
                              data-testid={`cred-revoke-btn-${c.id}`}
                            >
                              Revoke
                            </button>
                          ) : c.status === 'pending' && !c.unifi_credential_id ? (
                            <button
                              type="button"
                              onClick={() => setRevokeTarget(c)}
                              className="inline-flex min-h-[1.5rem] items-center text-on-surface-variant hover:underline font-label-md text-label-md"
                              data-testid={`cred-cancel-btn-${c.id}`}
                            >
                              Cancel
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {emp.credentials.filter((c) => c.status === 'revoked').length > 0 && (
              <p className="px-4 py-2 font-body-sm text-body-sm text-on-surface-variant">
                {emp.credentials.filter((c) => c.status === 'revoked').length} revoked credential
                {emp.credentials.filter((c) => c.status === 'revoked').length !== 1 ? 's' : ''}{' '}
                hidden
              </p>
            )}
          </>
        )}
      </section>

      {editOpen ? (
        <EditEmployeeModal
          emp={emp}
          scopeOpts={scopeOpts}
          onCancel={() => setEditOpen(false)}
          onDone={(msg) => {
            setEditOpen(false);
            setToast(msg);
            reload();
          }}
        />
      ) : null}

      {statusAction ? (
        <StatusActionModal
          kind={statusAction}
          emp={emp}
          scopeOpts={scopeOpts}
          onCancel={() => setStatusAction(null)}
          onDone={(msg) => {
            setStatusAction(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}

      {issueOpen ? (
        <IssueCredentialModal
          empId={emp.id}
          touchPolicyEnabled={touchPolicyEnabled}
          thirdPartyNfcEnabled={thirdPartyNfcEnabled}
          scopeOpts={scopeOpts}
          onCancel={() => setIssueOpen(false)}
          onDone={(msg) => {
            setIssueOpen(false);
            setToast(msg);
            reload();
          }}
        />
      ) : null}

      {enrollOpen ? (
        <EnrollFobModal
          empId={emp.id}
          personName={emp.name}
          thirdPartyNfcEnabled={thirdPartyNfcEnabled}
          enrollmentReaderMode={enrollmentReaderMode}
          enrollmentReaderId={enrollmentReaderId}
          enrollmentReaderName={enrollmentReaderName}
          scopeOpts={scopeOpts}
          onCancel={() => setEnrollOpen(false)}
          onDone={(msg) => {
            setEnrollOpen(false);
            setToast(msg);
            reload();
          }}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeCredentialModal
          empId={emp.id}
          cred={revokeTarget}
          scopeOpts={scopeOpts}
          onCancel={() => setRevokeTarget(null)}
          onDone={(msg) => {
            setRevokeTarget(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md text-on-surface-variant uppercase mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}

function RecentActivityPanel({ activity }: { activity: AccessActivitySummary[] }) {
  return (
    <section
      aria-label="Recent activity"
      className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-outline-variant">
        <h2 className="font-headline-sm text-headline-sm text-on-surface">Recent Activity</h2>
      </div>
      {activity.length === 0 ? (
        <div className="px-4 py-4 text-on-surface-variant font-body-md text-body-md">
          No activity recorded.
        </div>
      ) : (
        <ol className="divide-y divide-outline-variant">
          {activity.map((event) => (
            <li key={event.event_id} className="px-4 py-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="font-body-md text-body-md text-on-surface">
                  {activityLocation(event)}
                </div>
                <time
                  className="font-body-sm text-body-sm text-on-surface-variant"
                  dateTime={event.occurred_at}
                >
                  {activityTime(event.occurred_at)}
                </time>
              </div>
              <div className="font-body-sm text-body-sm text-on-surface-variant">
                {event.type}
                {event.severity !== 'info' ? ` · ${event.severity}` : ''}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function EmployeeAccessZonesPanel({
  emp,
  scheduleName,
  schedules,
  onChanged,
  onToast,
  scopeOpts,
}: {
  emp: EmployeeDetail;
  scheduleName: string | null;
  schedules: ScheduleSummary[];
  onChanged: () => void;
  onToast: (toast: string) => void;
  scopeOpts?: ApiRequestInit;
}) {
  const [rows, setRows] = useState<{
    assigned: EmployeeAccessZoneRow[];
    available: EmployeeAccessZoneRow[];
  } | null>(null);
  const [scheduleId, setScheduleId] = useState(emp.schedule_id ?? '');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    setScheduleId(emp.schedule_id ?? '');
  }, [emp.schedule_id]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    apiClient
      .get<{
        data: {
          assigned: EmployeeAccessZoneRow[];
          available: EmployeeAccessZoneRow[];
        };
      }>(`/api/v1/employees/${emp.id}/access-zones`, scopeOpts)
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows({ assigned: [], available: [] });
          setError(err instanceof ApiError ? err.message : 'Failed to load access zones.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [emp.id, reloadTick, scopeOpts]);

  async function mutate(groupId: string, mode: 'add' | 'remove') {
    setBusyId(groupId);
    setError(null);
    try {
      if (mode === 'add') {
        await apiClient.post(`/api/v1/employees/${emp.id}/access-zones/${groupId}`, {}, scopeOpts);
      } else {
        await apiClient.delete(`/api/v1/employees/${emp.id}/access-zones/${groupId}`, scopeOpts);
      }
      reload();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Access-zone update failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function saveSchedule() {
    setSavingSchedule(true);
    setError(null);
    try {
      await apiClient.patch(
        `/api/v1/employees/${emp.id}`,
        { schedule_id: scheduleId === '' ? null : scheduleId },
        scopeOpts,
      );
      onToast('Schedule updated.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Schedule update failed.');
    } finally {
      setSavingSchedule(false);
    }
  }

  const scheduleChanged = scheduleId !== (emp.schedule_id ?? '');
  const hasAssignedZones = (rows?.assigned.length ?? 0) > 0;

  return (
    <section
      id="access-zones"
      aria-label="Access policy"
      className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden"
    >
      <div className="px-4 py-4 border-b border-outline-variant">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-headline-sm text-headline-sm text-on-surface">Access policy</h2>
              {rows && !hasAssignedZones ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-tertiary-fixed px-2 py-1 font-label-md text-label-md text-on-tertiary-fixed">
                  <Icon name="warning" className="text-sm" />
                  No zones assigned
                </span>
              ) : null}
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant mt-1">
              Choose when this employee can enter, then choose the door zones they can use.
            </p>
          </div>
          <div className="w-full lg:max-w-md">
            <Field label="Schedule" htmlFor="emp-access-schedule">
              <div className="flex flex-col gap-2 sm:flex-row">
                <select
                  id="emp-access-schedule"
                  value={scheduleId}
                  onChange={(e) => setScheduleId(e.target.value)}
                  className="min-w-0 flex-1 px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
                  disabled={savingSchedule}
                >
                  <option value="">No schedule</option>
                  {schedules.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => void saveSchedule()}
                  disabled={!scheduleChanged || savingSchedule}
                >
                  {savingSchedule ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </Field>
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              Current schedule: {scheduleName ?? 'none'}
            </p>
          </div>
        </div>
      </div>
      {error ? (
        <div className="px-4 py-3 text-error font-body-md text-body-md" role="alert">
          {error}
        </div>
      ) : null}
      {!rows ? (
        <div className="p-6 flex justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="divide-y divide-outline-variant">
          {rows.assigned.length === 0 ? (
            <div className="px-4 py-4">
              <div className="rounded-lg border border-tertiary bg-tertiary-fixed/40 px-3 py-3 text-on-surface">
                <div className="flex items-start gap-2">
                  {/* UX-028: text-tertiary resolves to #000000 in the light palette, so
                      this "warning" was drawn in the same ink as body text and
                      carried no signal. `warning` is the token that exists for
                      exactly this. */}
                  <Icon name="warning" className="mt-0.5 text-warning" />
                  <div>
                    <p className="font-label-md text-label-md">No access zones assigned</p>
                    <p className="font-body-sm text-body-sm text-on-surface-variant">
                      This employee has no door access until a zone is added below.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            rows.assigned.map((zone) => (
              <AccessZoneActionRow
                key={zone.id}
                zone={zone}
                action="remove"
                busy={busyId === zone.id}
                onClick={() => void mutate(zone.id, 'remove')}
              />
            ))
          )}
          {rows.available.length > 0 ? (
            <div className="bg-surface-container-low">
              <div className="px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">
                Available
              </div>
              {rows.available.map((zone) => (
                <AccessZoneActionRow
                  key={zone.id}
                  zone={zone}
                  action="add"
                  busy={busyId === zone.id}
                  onClick={() => void mutate(zone.id, 'add')}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

function AccessZoneActionRow({
  zone,
  action,
  busy,
  onClick,
}: {
  zone: EmployeeAccessZoneRow;
  action: 'add' | 'remove';
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div>
        <div className="font-body-md text-body-md text-on-surface">{zone.name}</div>
        <div className="font-body-sm text-body-sm text-on-surface-variant">
          {accessZoneUpdateLabel(zone)}
          {zone.last_sync_error ? ` · ${zone.last_sync_error}` : ''}
        </div>
      </div>
      <Button
        variant={action === 'add' ? 'primary' : 'ghost'}
        size="sm"
        onClick={onClick}
        disabled={busy}
      >
        {busy
          ? action === 'add'
            ? 'Adding...'
            : 'Revoking...'
          : action === 'add'
            ? 'Add'
            : 'Revoke access'}
      </Button>
    </div>
  );
}

// ---------- Edit ----------

function EditEmployeeModal({
  emp,
  scopeOpts,
  onCancel,
  onDone,
}: {
  emp: EmployeeDetail;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key } = useIdempotencyKey();
  const [name, setName] = useState(emp.name);
  const [role, setRole] = useState<EmployeeRole>(emp.role);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (name.trim().length === 0) {
      setError('Name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        role,
      };
      await apiClient.withIdempotencyKey(key).patch(`/api/v1/employees/${emp.id}`, body, scopeOpts);
      onDone('Employee updated.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Update failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title={`Edit ${emp.name}`} onCancel={onCancel} testId="edit-employee-modal">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Name" htmlFor="edit-emp-name">
          <input
            id="edit-emp-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
            data-testid="edit-emp-name-input"
          />
        </Field>
        <Field label="Role" htmlFor="edit-emp-role">
          <select
            id="edit-emp-role"
            value={role}
            onChange={(e) => setRole(e.target.value as EmployeeRole)}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
          >
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
          </select>
        </Field>
        <input type="hidden" value={key} data-testid="edit-idempotency-key" readOnly />
        {error ? (
          <div role="alert" className="text-error font-body-md text-body-md">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting} data-testid="edit-submit">
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Status (suspend/reactivate/delete) ----------

function StatusActionModal({
  kind,
  emp,
  scopeOpts,
  onCancel,
  onDone,
}: {
  kind: 'suspend' | 'reactivate' | 'offboard';
  emp: EmployeeDetail;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key } = useIdempotencyKey();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const irreversible = kind === 'offboard';

  const title =
    kind === 'suspend'
      ? `Suspend ${emp.name}?`
      : kind === 'reactivate'
        ? `Reactivate ${emp.name}?`
        : `Delete ${emp.name}?`;
  const body =
    kind === 'offboard'
      ? 'This removes the person from live doors and revokes their credentials so fobs can be reassigned. The portal record is retained for history.'
      : kind === 'suspend'
        ? 'Suspending blocks access immediately while keeping credentials assigned to this person for audit.'
        : 'Reactivation restores access. If access cannot be restored automatically, re-invite instead.';

  async function go() {
    setSubmitting(true);
    setError(null);
    try {
      const target =
        kind === 'suspend' ? 'suspended' : kind === 'reactivate' ? 'active' : 'offboarded';
      await apiClient
        .withIdempotencyKey(key)
        .patch(`/api/v1/employees/${emp.id}`, { status: target }, scopeOpts);
      onDone(
        kind === 'suspend'
          ? 'Employee suspended.'
          : kind === 'reactivate'
            ? 'Employee reactivated.'
            : 'Employee deleted.',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title={title} onCancel={onCancel} testId={`status-${kind}-modal`}>
      <div className="space-y-4">
        <p className="font-body-md text-body-md text-on-surface">{body}</p>
        {irreversible ? (
          <label className="flex items-center gap-2 font-body-md text-body-md text-on-surface">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid={`${kind}-confirm-check`}
            />
            I understand this releases active credentials for reassignment.
          </label>
        ) : null}
        <input type="hidden" value={key} data-testid={`${kind}-idempotency-key`} readOnly />
        {error ? (
          <div role="alert" className="text-error font-body-md text-body-md">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={irreversible ? 'danger' : 'primary'}
            onClick={go}
            disabled={submitting || (irreversible && !confirmed)}
            data-testid={`${kind}-confirm-btn`}
          >
            {kind === 'suspend' ? 'Suspend' : kind === 'reactivate' ? 'Reactivate' : 'Delete'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ---------- Issue Phone KeyPass ----------

function IssueCredentialModal({
  empId,
  touchPolicyEnabled,
  thirdPartyNfcEnabled,
  scopeOpts,
  onCancel,
  onDone,
}: {
  empId: string;
  touchPolicyEnabled: boolean;
  thirdPartyNfcEnabled: boolean;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key } = useIdempotencyKey();
  const [type, setType] = useState<CredentialType>(
    touchPolicyEnabled ? 'mobile_key' : thirdPartyNfcEnabled ? 'nfc_card' : 'pin_code',
  );
  const [label, setLabel] = useState('');
  const [cardToken, setCardToken] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [touchPassId, setTouchPassId] = useState('');
  const [touchPasses, setTouchPasses] = useState<TouchPassRow[]>([]);
  const [touchPassLoadError, setTouchPassLoadError] = useState<string | null>(null);
  // Distinct from touchPasses.length: a tenant can have every allocated pass
  // already in use while still being entitled to Phone KeyPass issuance.
  const [tenantHasTouchCredentials, setTenantHasTouchCredentials] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phoneKeyPassUnavailable = type === 'mobile_key' && !touchPassId.trim();
  const pinCodeUnavailable = type === 'pin_code' && !isValidPinCode(pinCode);

  useEffect(() => {
    if (!touchPolicyEnabled) {
      setTouchPasses([]);
      setTouchPassId('');
      setTenantHasTouchCredentials(false);
      return;
    }
    let cancelled = false;
    setTouchPassLoadError(null);
    apiClient
      .get<{ data: TouchPassRow[]; meta?: { tenant_has_touch_credentials?: boolean } }>(
        '/api/v1/employees/credentials/touch-passes/assignable',
        scopeOpts,
      )
      .then((res) => {
        if (cancelled) return;
        setTouchPasses(res.data);
        setTouchPassId((current) => current || res.data[0]?.id || '');
        setTenantHasTouchCredentials(res.meta?.tenant_has_touch_credentials ?? res.data.length > 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTouchPasses([]);
        setTouchPassId('');
        setTenantHasTouchCredentials(false);
        setTouchPassLoadError(
          err instanceof ApiError ? err.message : 'Could not load touch credentials.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [scopeOpts, touchPolicyEnabled]);


  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { type };
      if (label.trim().length > 0) body.label = label.trim();
      if (type === 'mobile_key') {
        if (!touchPolicyEnabled || !tenantHasTouchCredentials) {
          throw new Error('Phone KeyPass is disabled by admin policy or not allocated to this tenant.');
        }
        if (touchPassId.trim().length === 0) {
          throw new Error('Choose a Phone KeyPass before issuing.');
        }
        body.touch_pass_id = touchPassId.trim();
      }
      if (type === 'nfc_card') {
        if (!thirdPartyNfcEnabled) {
          throw new Error('Third-party NFC is disabled by admin policy.');
        }
        if (cardToken.trim().length > 0) {
          body.card_token = cardToken.trim();
        }
      }
      if (type === 'pin_code') {
        if (!isValidPinCode(pinCode)) {
          throw new Error('Enter a 4 to 8 digit PIN code.');
        }
        body.pin_code = pinCode.trim();
      }
      await apiClient
        .withIdempotencyKey(key)
        .post(`/api/v1/employees/${empId}/credentials`, body, scopeOpts);
      onDone('Credential issued.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Issue failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Issue Phone KeyPass" onCancel={onCancel} testId="issue-credential-modal">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Type" htmlFor="cred-type">
          <select
            id="cred-type"
            value={type}
            onChange={(e) => setType(e.target.value as CredentialType)}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
            data-testid="cred-type-select"
          >
            {thirdPartyNfcEnabled ? <option value="nfc_card">NFC card / fob</option> : null}
            <option value="pin_code">PIN code</option>
            {touchPolicyEnabled ? <option value="mobile_key">Phone KeyPass</option> : null}
          </select>
        </Field>
        <Field label="Label (optional)" htmlFor="cred-label">
          <input
            id="cred-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={120}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
          />
        </Field>
        {type === 'mobile_key' ? (
          <Field label="Phone KeyPass" htmlFor="touch-pass-id">
            <select
              id="touch-pass-id"
              value={touchPassId}
              onChange={(e) => setTouchPassId(e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
              data-testid="touch-pass-select"
            >
              <option value="">Select a Phone KeyPass</option>
              {touchPasses.map((row) => (
                <option key={row.id} value={row.id}>
                  {(row.card_name && row.card_name.trim().length > 0
                    ? row.card_name
                    : row.card_id) ?? row.id}
                </option>
              ))}
            </select>
            {touchPassLoadError ? (
              <p className="mt-2 text-error font-body-md text-body-md" role="alert">
                {touchPassLoadError}
              </p>
            ) : touchPasses.length === 0 ? (
              <p className="mt-2 text-on-surface-variant font-body-md text-body-md">
                No tenant-allocated Phone KeyPasses are available right now.
              </p>
            ) : null}
          </Field>
        ) : null}
        {type === 'nfc_card' ? (
          <Field label="Card code (required for direct issue)" htmlFor="cred-token">
            <input
              id="cred-token"
              type="text"
              value={cardToken}
              onChange={(e) => setCardToken(e.target.value)}
              maxLength={120}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface font-code text-code"
              data-testid="cred-token-input"
            />
          </Field>
        ) : null}
        {type === 'pin_code' ? (
          <Field label="PIN code" htmlFor="cred-pin-code">
            <input
              id="cred-pin-code"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              pattern="[0-9]*"
              value={pinCode}
              onChange={(e) => setPinCode(normalizePinCodeInput(e.target.value))}
              maxLength={8}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface font-code text-code"
              data-testid="cred-pin-code-input"
            />
            <p className="mt-2 text-on-surface-variant font-body-sm text-body-sm">
              Use 4 to 8 digits. This is the code the person will type at a keypad.
            </p>
          </Field>
        ) : null}
        <input type="hidden" value={key} data-testid="issue-idempotency-key" readOnly />
        {error ? (
          <div role="alert" className="text-error font-body-md text-body-md">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitting || phoneKeyPassUnavailable || pinCodeUnavailable}
            data-testid="issue-submit"
          >
            {submitting ? 'Issuing…' : type === 'mobile_key' ? 'Issue Phone KeyPass' : 'Issue'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Revoke credential ----------

function RevokeCredentialModal({
  empId,
  cred,
  scopeOpts,
  onCancel,
  onDone,
}: {
  empId: string;
  cred: CredentialRow;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key } = useIdempotencyKey();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient
        .withIdempotencyKey(key)
        .delete(`/api/v1/employees/${empId}/credentials/${cred.id}`, scopeOpts);
      onDone('Credential revoked.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Revoke failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Revoke credential?" onCancel={onCancel} testId="revoke-credential-modal">
      <div className="space-y-4">
        <p className="font-body-md text-body-md text-on-surface">
          Revoke the{' '}
          {credentialTypeLabel(cred.type, {
            unifiCredentialId: cred.unifi_credential_id,
          })}{' '}
          credential
          {cred.label ? ` "${cred.label}"` : ''}? This disables it for live access and marks it
          revoked here.
        </p>
        <input type="hidden" value={key} data-testid="revoke-idempotency-key" readOnly />
        {error ? (
          <div role="alert" className="text-error font-body-md text-body-md">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={go}
            disabled={submitting}
            data-testid="revoke-confirm-btn"
          >
            {submitting ? 'Revoking…' : 'Revoke'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// EnrollFobModal moved to ../components/EnrollFobModal.tsx (shared with the
// "enroll fob now?" step in CreateEmployeeModal).
